import { internalApiClient } from '@/lib/api-client'
import type { TimeEntry } from '@/shared/types/entities'
import type { ApiResponse, CursorPaginatedResponse, CursorPaginationParams } from '@/shared/types/api'

export interface CreateTimeEntryDto {
  projectId: string
  taskId?: string
  description: string
  startTime: string
  endTime: string
  tagIds?: string[]
  isBillable?: boolean
  jiraIssueKey?: string
}

export interface UpdateTimeEntryDto extends Partial<CreateTimeEntryDto> {}

export interface TimeEntryFilters {
  startDate?: string
  endDate?: string
  projectId?: string
  taskId?: string
  tagIds?: string[]
  isBillable?: boolean
  search?: string
}

export const timeEntryService = {
  async getTimeEntries(
    workspaceId: string,
    filters?: TimeEntryFilters,
    pagination?: CursorPaginationParams
  ): Promise<CursorPaginatedResponse<TimeEntry>> {
    const params: Record<string, any> = {
      ...filters,
      limit: pagination?.limit || 50,
    }
    
    if (pagination?.cursor) {
      params.cursor = pagination.cursor
    }
    
    if (pagination?.direction) {
      params.direction = pagination.direction
    }
    
    if (pagination?.sortBy) {
      params.sortBy = pagination.sortBy
    }
    
    if (pagination?.sortOrder) {
      params.sortOrder = pagination.sortOrder
    }
    
    const response = await internalApiClient.get<CursorPaginatedResponse<TimeEntry>>(`/workspaces/${workspaceId}/time-entries`, {
      params,
    })
    return response.data
  },

  async getTimeEntryById(workspaceId: string, id: string): Promise<TimeEntry> {
    const response = await internalApiClient.get<ApiResponse<TimeEntry>>(`/workspaces/${workspaceId}/time-entries/${id}`)
    return response.data.data
  },

  async createTimeEntry(workspaceId: string, data: CreateTimeEntryDto): Promise<TimeEntry> {
    const response = await internalApiClient.post<ApiResponse<TimeEntry>>(`/workspaces/${workspaceId}/time-entries`, data)
    return response.data.data
  },

  async updateTimeEntry(workspaceId: string, id: string, data: UpdateTimeEntryDto): Promise<TimeEntry> {
    const response = await internalApiClient.patch<ApiResponse<TimeEntry>>(`/workspaces/${workspaceId}/time-entries/${id}`, data)
    return response.data.data
  },

  async deleteTimeEntry(workspaceId: string, id: string): Promise<void> {
    await internalApiClient.delete(`/workspaces/${workspaceId}/time-entries/${id}`)
  },

  async getRecentEntries(workspaceId: string, limit = 10): Promise<TimeEntry[]> {
    const response = await internalApiClient.get<{ data: TimeEntry[] }>(`/workspaces/${workspaceId}/time-entries/recent`, {
      params: { limit },
    })
    return response.data.data
  },
}

export default timeEntryService
